import { getIconPath } from '@/common'
import Data from '@/data'
import Store from '@/store'
import { WithGemTooltip, WithItemTooltip } from '@/tooltip'
import { CallbackWrap } from '@/types/common'
import { SkillsEmbedSkill } from '@/types/embed'
import { Gem } from '@/types/item'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { ActionButton, Icon } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import update from 'immutability-helper'
import React from 'react'
import { useEmbedLink } from '../utils/copy'
import { useEditPlanner } from '../utils/edit'
import { PlannerWidget } from '../utils/widget/widget'

import { InventorySlotType } from '@/data/enums'
import { ItemDefinition } from '@/data/types'
import { gemItemIcon, gemName } from '@/logic/gem'
import { PlannerEditSkillSets } from './edit'
import { PlannerEditSkill } from './edit-skill'
import styles from './skills.module.scss'

function SkillGem({ gem }: { gem: Gem }) {
  const item = Data.gems[gem.id]
  if (!item || !item.base_item) return null
  return (
    <WithGemTooltip gem={gem}>
      <div
        className={styles.SkillGem}
        style={{
          backgroundImage: `url(${getIconPath(gemItemIcon(item)!)})`
        }}
      />
    </WithGemTooltip>
  )
}

export enum ItemImageType {
  Any = 0,
  OneHand,
  OneHandRanged,
  TwoHand,
  TwoHandRanged,
  Amulet,
  BodyArmour,
  Belt,
  Boots,
  Flask,
  Gloves,
  Helmet,
  Quiver,
  Ring,
  Shield
}

export function slotIconIndex(slot: InventorySlotType | undefined, item?: ItemDefinition) {
  switch (slot) {
    case 'Amulet':
      return ItemImageType.Amulet
    case 'BodyArmour':
      return ItemImageType.BodyArmour
    case 'Boots':
      return ItemImageType.Boots
    case 'Gloves':
      return ItemImageType.Gloves
    case 'Helm':
      return ItemImageType.Helmet
    case 'Ring':
    case 'Ring2':
      return ItemImageType.Ring
    case 'Weapon':
    case 'Offhand':
    case 'Weapon2':
    case 'Offhand2':
      if (item?.tags.includes('one_hand_weapon')) {
        if (item.tags.includes('ranged')) {
          return ItemImageType.OneHandRanged
        } else {
          return ItemImageType.OneHand
        }
      } else if (item?.tags.includes('two_hand_weapon')) {
        if (item.tags.includes('ranged')) {
          return ItemImageType.TwoHandRanged
        } else {
          return ItemImageType.TwoHand
        }
      } else if (item?.tags.includes('shield')) {
        return ItemImageType.Shield
      }
      return slot === 'Weapon' || slot === 'Weapon2' ? ItemImageType.OneHand : ItemImageType.Shield
    default:
      return ItemImageType.Any
  }
}

function SlotIcon({ slot }: { slot?: InventorySlotType }) {
  const slotId = Store.usePlanner((planner) =>
    slot ? planner.equipment.variants[planner.equipment.active]?.items[slot] : undefined
  )
  const slotItem = Store.useItem(slotId)
  const base = slotItem ? Data.items[slotItem.base] : slotItem
  const iconIndex = slotIconIndex(slot, base)

  const content = (
    <div className={styles.SlotIcon} style={{ backgroundPositionX: `-${iconIndex}em` }}>
      {(slot === 'Weapon2' || slot === 'Offhand2') && <div className={styles.SlotIcon__second}>II</div>}
    </div>
  )
  if (slotItem) return <WithItemTooltip item={slotItem}>{content}</WithItemTooltip>
  return content
}

function PlannerSkill({
  index,
  skill,
  onChange
}: {
  index: number
  skill: SkillsEmbedSkill | undefined
  onChange?: (value: CallbackWrap<SkillsEmbedSkill | undefined>) => void
}) {
  const canEditPlanner = useEditPlanner()
  const [editing, setEditing] = React.useState(false)

  const activeGem = skill?.gems.find((gem) => {
    const item = Data.gems[gem.id]
    if (!item) return false
    return item.active_skill || item.tags?.includes('trigger')
  })

  const activeGemItem = activeGem ? Data.gems[activeGem.id] : undefined

  const isDragDisabled = !skill || !canEditPlanner

  return (
    <>
      <Draggable draggableId={index.toString()} index={index} isDragDisabled={isDragDisabled}>
        {(provided) => (
          <div
            className={classNames(styles.SkillEntry, canEditPlanner && styles.editable, !skill && styles.empty)}
            onClick={canEditPlanner ? () => setEditing(true) : undefined}
            {...provided.dragHandleProps}
            {...provided.draggableProps}
            ref={provided.innerRef}>
            {!isDragDisabled ? (
              <div>
                <Icon className={styles.SkillEntry__dragIcon} icon="icons/drag-2.svg" width={11} height={15} />
              </div>
            ) : null}
            {skill ? <SlotIcon slot={skill.slot} /> : <div className={styles.SkillEntry__emptyIcon} />}
            <div className={styles.SkillEntry__name}>
              <div>{skill ? (activeGemItem ? gemName(activeGemItem) : '') : 'Add Skill...'}</div>
            </div>
            <div className={styles.SkillEntry__gems}>
              {skill?.gems.map((gem, index) => (
                <SkillGem gem={gem} key={index} />
              ))}
            </div>
          </div>
        )}
      </Draggable>

      {editing && onChange ? (
        <PlannerEditSkill skill={skill} onChange={onChange} onClose={() => setEditing(false)} />
      ) : undefined}
    </>
  )
}

export function PlannerSkills({ isPob = false }: { isPob?: boolean; showAsSteps?: boolean }) {
  const isEditEnabled = useEditPlanner()
  const canEditPlanner = !isPob && isEditEnabled
  const skills = Store.usePlanner((planner) => planner.skills)
  const step = skills.steps[skills.step] ?? { name: 'Unknown', skills: [] }
  const dispatch = Store.useDispatch()
  const skillList = canEditPlanner ? [...step.skills, undefined] : step.skills

  const embedLink = useEmbedLink('skills')

  const onDragEnd = React.useCallback(
    ({ destination, source }: DropResult) => {
      if (!destination || source.index === destination.index) return
      dispatch.setPlanner((planner) =>
        update(planner, {
          skills: {
            steps: {
              [planner.skills.step]: {
                skills: {
                  $apply(skills: SkillsEmbedSkill[]) {
                    skills = skills.slice()
                    const src = skills.splice(source.index, 1)
                    skills.splice(destination.index, 0, ...src)
                    return skills
                  }
                }
              }
            }
          }
        })
      )
    },
    [dispatch]
  )

  return (
    <PlannerWidget
      title="Skills"
      actions={
        <>
          {canEditPlanner ? (
            <ActionButton icon="edit" label="Edit" modalConfirmButton={true}>
              <PlannerEditSkillSets />
            </ActionButton>
          ) : undefined}
        </>
      }
      copyLink={embedLink}>
      <div className={styles.PlannerSkills}>
        <div className={styles.PlannerSkills__header}>
          <button
            disabled={skills.step <= 0}
            onClick={() =>
              dispatch.setPlanner((planner) =>
                update(planner, {
                  skills: {
                    step: { $apply: (v) => Math.max(0, v - 1) }
                  }
                })
              )
            }>
            <FontAwesomeIcon icon={faChevronLeft} /> Prev
          </button>
          <div className={styles.PlannerSkills__name}>
            {skills.step + 1}. {step.name || 'Skills'}
          </div>
          <button
            disabled={skills.step >= skills.steps.length - 1}
            onClick={() =>
              dispatch.setPlanner((planner) =>
                update(planner, {
                  skills: {
                    step: {
                      $apply: (v) => Math.min(skills.steps.length - 1, v + 1)
                    }
                  }
                })
              )
            }>
            Next <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="skill-list">
            {(provided) => (
              <div className={styles.PlannerSkills__list} {...provided.droppableProps} ref={provided.innerRef}>
                {skillList.map((skill, index) => (
                  <PlannerSkill
                    key={index}
                    index={index}
                    skill={skill}
                    onChange={(value: CallbackWrap<SkillsEmbedSkill | undefined>) =>
                      dispatch.setPlanner((planner) => {
                        const prev = planner.skills.steps[planner.skills.step]?.skills[index]
                        if (typeof value === 'function') value = value(prev)
                        if (value === prev) return planner
                        return update(planner, {
                          skills: {
                            steps: {
                              [planner.skills.step]: {
                                skills: value
                                  ? {
                                      [index]: { $set: value }
                                    }
                                  : {
                                      $splice: [[index, 1]]
                                    }
                              }
                            }
                          }
                        })
                      })
                    }
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </PlannerWidget>
  )
}
